import cx from 'classnames';
import type { CSSProperties, FC, ReactNode, RefObject, SyntheticEvent } from 'react';
import { useMemo } from 'react';

import { TABLE_HEADER_ZINDEX } from '@zen/Layout';
import type { SortingOrder } from '@zen/types';
import { useHover } from '@zen/utils/hooks/useHover';
import { createTrackingLabelAttribute, createTrackingParentAttribute } from '@zen/utils/tracking';

import Loading from '../../../../Loading';
import { RESIZE_LINE_HEIGHT } from '../../../constants';
import { useColumnResizeIndicator } from '../../../hooks/useColumnResizeIndicator';
import type { TableHeaderProps } from '../../../types';
import DragHandle from '../../DragHandle';
import { FIXED_BOUNDARY_SHADOW_CLASSNAME, getFixedBoundaryStyle } from '../../fixedBoundaryShadow';
import SortIndicator from '../../SortIndicator';

interface Props {
  children?: ReactNode;
  className?: string;
  columnKey?: string;
  draggable?: boolean;
  fixed?: 'left' | 'right' | boolean;
  fixedOffset?: number;
  header?: TableHeaderProps;
  isDragging: boolean;
  isFixedBoundary?: boolean;
  isOver: boolean;
  isSorted?: boolean;
  loading?: boolean;
  offsetElementRef: RefObject<HTMLDivElement | null>;
  onClick: (event: SyntheticEvent) => void;
  onResizeEnd?: (width: number) => void;
  resizable?: boolean;
  resizableWidth?: number;
  rightAligned: boolean;
  setCellRef: (element: HTMLTableHeaderCellElement) => void;
  setDragRef: (element: HTMLDivElement) => void;
  setResizableWidth: (width: number) => void;
  sortDirection?: SortingOrder;
  sortable?: boolean;
  style: CSSProperties;
  title: ReactNode;
}

const StyledTableHeaderCell: FC<Props> = (props) => {
  const [ref, isHovered] = useHover();
  const {
    children,
    className,
    columnKey,
    draggable,
    fixed,
    fixedOffset = 0,
    header,
    isDragging,
    isFixedBoundary = false,
    isOver,
    isSorted,
    loading = false,
    offsetElementRef,
    onClick,
    onResizeEnd,
    resizable,
    resizableWidth,
    rightAligned = false,
    setCellRef,
    setDragRef,
    setResizableWidth,
    sortable,
    sortDirection,
    style,
    title,
    ...rest
  } = props;

  // Track column resize visual indicator state
  const { isResizing, resizeDelta } = useColumnResizeIndicator({
    header,
    onResizeEnd,
    setResizableWidth
  });

  const fixedDirection = typeof fixed === 'string' ? fixed : fixed ? 'left' : undefined;

  const cellClassNames: string = cx(
    'group',
    'h-10',
    'align-middle',
    'whitespace-nowrap',
    'text-grey-dark text-xs font-bold',
    'border-b border-solid border-grey-lighter',
    'first:[&_[data-testid="column-title"]]:pl-6',
    {
      'hover:text-azure-base': sortable,
      'opacity-50': isDragging,
      'bg-grey-lightest': !isOver,
      'bg-azure-lighter': isOver,
      'cursor-pointer': sortable,
      relative: resizable
    },
    className
  );

  const cellStyle: CSSProperties = {
    ...style,
    ...(fixedDirection && {
      position: 'sticky',
      [fixedDirection]: fixedOffset,
      zIndex: 11,
      width: '1%'
    }),
    ...(isResizing && {
      zIndex: 9999,
      overflow: 'visible'
    })
  };

  const renderSort = useMemo(() => {
    if (!sortable) {
      return null;
    }

    const sortIndicatorClassName: string = cx('z-20', {
      'text-azure-base': isSorted,
      'ml-auto': rightAligned
    });

    return loading ? (
      <Loading color="grey" size="smallest" />
    ) : (
      <SortIndicator className={sortIndicatorClassName} direction={sortDirection} isHovered={isHovered} isResizing={isResizing} />
    );
  }, [sortable, loading, isHovered, isSorted, sortDirection, rightAligned, isResizing]);

  const renderStyledHeader = (): ReactNode => {
    const titleClassName: string = cx({
      'text-right': rightAligned
    });

    const isUtilityColumn = columnKey === 'expandColumn' || columnKey === 'bulkSelection';

    const contentClassNames: string = cx('flex items-center h-10 px-4 space-x-2', {
      'justify-end': rightAligned,
      '!pr-0': isUtilityColumn
    });
    const contentDraggableClassNames: string = cx(
      'ml-auto transition-opacity opacity-0 group-hover:opacity-100',
      TABLE_HEADER_ZINDEX
    );

    return (
      <>
        <div ref={ref} className={contentClassNames} data-testid="column-title">
          {rightAligned && renderSort}
          <div className={titleClassName} {...createTrackingLabelAttribute()}>
            {title}
          </div>
          {!rightAligned && renderSort}

          {draggable && (
            <DragHandle ref={setDragRef} className={contentDraggableClassNames} isDragging={isDragging} isResizing={isResizing} />
          )}
        </div>
        {children}
      </>
    );
  };

  // TanStack resize handle - wider clickable area with narrow visual indicator
  const renderResizeHandle = (): ReactNode => {
    if (!resizable || !header) {
      return null;
    }

    return (
      <div
        className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none touch-none z-10 group/resize -mr-1"
        data-component="table-resize-handle"
        data-testid="resize-handle"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={() => header.column.resetSize()}
        onMouseDown={header.getResizeHandler()}
        onTouchStart={header.getResizeHandler()}
      >
        {/* Hover indicator - small pill at top */}
        <div
          className={cx(
            'absolute right-0.5 top-2 w-1 h-6 rounded-full transition-colors',
            'group-hover/resize:bg-azure-base',
            isResizing && 'bg-azure-base'
          )}
        />
        {/* Resize line - extends down through the table when resizing, follows mouse */}
        {isResizing && (
          <div
            className="absolute top-0 w-px bg-azure-base"
            style={{ height: `${RESIZE_LINE_HEIGHT}px`, right: -resizeDelta + 2, zIndex: 9999 }}
          />
        )}
      </div>
    );
  };

  return (
    <th
      ref={setCellRef}
      onClick={onClick}
      {...rest}
      {...createTrackingParentAttribute('table-header')}
      className={cellClassNames}
      style={cellStyle}
    >
      {fixedDirection && isFixedBoundary && (
        <div aria-hidden="true" className={FIXED_BOUNDARY_SHADOW_CLASSNAME} style={getFixedBoundaryStyle(fixedDirection)} />
      )}
      {renderStyledHeader()}
      {renderResizeHandle()}
    </th>
  );
};

export default StyledTableHeaderCell;
